import { useEffect } from "react";
import { useEtlynCms } from "@etlyn/etlyn-cms";
import { useTranslation } from "react-i18next";
import { getCompanyId } from "../../companyContext";

interface Props {
  isMob: boolean;
}

const fallbackReviews = [
  {
    id: "static-1",
    author: "Bettina Donmez",
    title: "E-Commerce Platform Development",
    description:
      "We use Etlyn in every single one of our projects that requires content management. Our clients love it.",
    stars: 5,
  },
  {
    id: "static-2",
    author: "Ashley Brooks",
    title: "E-commerce Operations Manager",
    description:
      "Etlyn lets us say yes to our creative ideas. I\u2019m so used to hearing \u201Cno, that\u2019s not possible.\u201D Not anymore! It has really opened up how we engage with customers.",
    stars: 5,
  },
  {
    id: "static-3",
    author: "Omar Benseddik",
    title: "Co-founder @ Tinloof",
    description:
      "There are so many exciting combinations our company can create with Etlyn. Now that we have a structure, it\u2019s infinitely scalable.",
    stars: 5,
  },
];

/* ── avatar with initials ────────────────────────────────── */
const palette = ["bg-[#0D2B7E]", "bg-[#25408B]", "bg-[#3B6FC2]"];
const Avatar = ({ name, idx }: { name: string; idx: number }) => {
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${palette[idx % palette.length]}`}>
      {initials}
    </div>
  );
};

/* ── star rating ─────────────────────────────────────────── */
const Stars = ({ count = 5 }: { count?: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <svg key={i} className="h-3.5 w-3.5 text-[#FCC419]" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 0 0 .95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 0 0-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118L10.588 15.58a1 1 0 0 0-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 0 0-.364-1.118L2.056 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 0 0 .95-.69l1.293-3.957Z" />
      </svg>
    ))}
  </div>
);

export const Reviews = ({ isMob }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { state, actions } = useEtlynCms("reviews-core");
  const { approvedReviews } = state;
  const companyId = getCompanyId();

  useEffect(() => {
    if (companyId) actions.fetchApprovedReviews({ companyId });
  }, [actions, companyId]);

  const normalised = Array.isArray(approvedReviews) ? approvedReviews : [];
  const reviews = normalised.length > 0 ? normalised : fallbackReviews;

  return (
    <section className="py-16">
      {/* Section header */}
      <div className="text-center mb-12">
        <span className="inline-block text-xs font-semibold uppercase tracking-[0.15em] text-[#0D2B7E] mb-3">
          Testimonials
        </span>
        <h2 className="text-xl sm:text-2xl font-bold text-[#333D51] tracking-tight">
          Trusted by ambitious teams
        </h2>
      </div>

      {/* Grid */}
      <div className={`grid gap-5 ${isMob ? "grid-cols-1" : "md:grid-cols-3"}`}>
        {reviews.slice(0, 3).map((review: any, i: number) => (
          <div
            key={review.id || i}
            className="group relative flex flex-col rounded-2xl bg-white/80 backdrop-blur-sm p-6 ring-1 ring-[#DFDFE0]/60 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
          >
            {/* Decorative quote mark */}
            <svg className="absolute right-5 top-5 h-8 w-8 text-[#EEF1FB] transition-colors duration-300 group-hover:text-[#DBEAFE]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5 3.871 3.871 0 01-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5 3.871 3.871 0 01-2.748-1.179z" />
            </svg>

            {/* Stars */}
            <Stars count={review.stars || 5} />

            {/* Body */}
            <p className="mt-4 flex-1 text-sm leading-relaxed text-[#666D7C]">
              &ldquo;{review.description}&rdquo;
            </p>

            {/* Author */}
            <div className="mt-5 flex items-center gap-3 border-t border-[#DFDFE0]/40 pt-4">
              <Avatar name={review.author} idx={i} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#333D51] truncate">{review.author}</p>
                <p className="text-xs text-[#808692] truncate">{review.title}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
